import React, { lazy } from 'react'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'  // HashRouter replace BrowserRouter
import { ResetCSS } from '@pancakeswap/uikit'
import BigNumber from 'bignumber.js'
import useEagerConnect from 'hooks/useEagerConnect'
import { usePollBlockNumber } from 'state/block/hooks'
import { usePollCoreFarmData } from 'state/farms/hooks'
import { useFetchProfile } from 'state/profile/hooks'
import { DatePickerPortal } from 'components/DatePicker'
import GlobalStyle from './style/Global'
import Menu from './components/Menu'
import SuspenseWithChunkError from './components/SuspenseWithChunkError'
import { ToastListener } from './contexts/ToastsContext'
import PageLoader from './components/Loader/PageLoader'
import EasterEgg from './components/EasterEgg'
import history from './routerHistory'
// Views included in the main bundle
import Pools from './views/Pools'
import Swap from './views/Swap'
import {
  RedirectDuplicateTokenIds,
  RedirectOldAddLiquidityPathStructure,
  RedirectToAddLiquidity,
} from './views/AddLiquidity/redirects'
import RedirectOldRemoveLiquidityPathStructure from './views/RemoveLiquidity/redirects'
import { RedirectPathToSwapOnly, RedirectToSwap } from './views/Swap/redirects'
import GlobalCheckClaimStatus from './views/Collectibles/components/GlobalCheckClaimStatus'

// Route-based code splitting
// Only pool is included in the main bundle because of it's the most visited page
const Home = lazy(() => import('./views/Home'))
const Farms = lazy(() => import('./views/Farms'))
const FarmAuction = lazy(() => import('./views/FarmAuction'))
const Lottery = lazy(() => import('./views/Lottery'))
const Ifos = lazy(() => import('./views/Ifos'))
const NotFound = lazy(() => import('./views/NotFound'))
const Collectibles = lazy(() => import('./views/Collectibles'))
const Teams = lazy(() => import('./views/Teams'))
const Team = lazy(() => import('./views/Teams/Team'))
const Profile = lazy(() => import('./views/Profile'))
const TradingCompetition = lazy(() => import('./views/TradingCompetition'))
const Predictions = lazy(() => import('./views/Predictions'))
const Voting = lazy(() => import('./views/Voting'))
const Proposal = lazy(() => import('./views/Voting/Proposal'))
const CreateProposal = lazy(() => import('./views/Voting/CreateProposal'))
const AddLiquidity = lazy(() => import('./views/AddLiquidity'))
const Liquidity = lazy(() => import('./views/Pool'))
const PoolFinder = lazy(() => import('./views/PoolFinder'))
const RemoveLiquidity = lazy(() => import('./views/RemoveLiquidity'))
const ZeusSwap = lazy(() => import('./views/ZeusSwap'))
const ApeSwap = lazy(() => import('./views/ApeSwap'))
const ApolloSwap = lazy(() => import('./views/ApolloSwap'))
const ZeusPool = lazy(() => import('./views/ZeusPool'))
const ApePool = lazy(() => import('./views/ApePool'))
const ApolloPool = lazy(() => import('./views/ApolloPool'))
const PolygonSwap = lazy(() => import('./views/PolygonSwap'))
const PolygonPool = lazy(() => import('./views/PolygonPool'))
const EthereumSwap = lazy(() => import('./views/EthereumSwap'))
const EthereumPool = lazy(() => import('./views/EthereumPool'))

// This config is required for number formatting
BigNumber.config({
  EXPONENTIAL_AT: 1000,
  DECIMAL_PLACES: 80,
})

const App: React.FC = () => {
  usePollBlockNumber()
  useEagerConnect()
  useFetchProfile()
  usePollCoreFarmData()

  return (
    <BrowserRouter>
      <ResetCSS />
      <GlobalStyle />
      <GlobalCheckClaimStatus excludeLocations={['/collectibles']} />
      <Switch>
        <Route exact path={['/zeus', '/zeus-swap']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <ZeusSwap />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/ape', '/ape-swap']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <ApeSwap />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/apollo', '/apollo-swap']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <ApolloSwap />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/zeus-pool']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <ZeusPool />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/ape-pool']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <ApePool />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/apollo-pool']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <ApolloPool />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/polygon', '/polygon-swap']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <PolygonSwap />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/polygon-pool']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <PolygonPool />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/ethereum', '/ethereum-swap']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <EthereumSwap />
          </SuspenseWithChunkError>
        </Route>
        <Route exact path={['/ethereum-pool']}>
          <SuspenseWithChunkError fallback={<PageLoader />}>
            <EthereumPool />
          </SuspenseWithChunkError>
        </Route>
        <Route>
          <Menu>
            <SuspenseWithChunkError fallback={<PageLoader />}>
              <Switch>
                <Route path="/" exact>
                  <Home />
                </Route>
                <Route exact path="/farms/auction">
                  <FarmAuction />
                </Route>
                <Route path="/farms">
                  <Farms />
                </Route>
                <Route path="/pools">
                  <Pools />
                </Route>
                <Route path="/lottery">
                  <Lottery />
                </Route>
                <Route exact path="/voting">
                  <Voting />
                </Route>
                <Route exact path="/voting/proposal/create">
                  <CreateProposal />
                </Route>
                <Route path="/voting/proposal/:id">
                  <Proposal />
                </Route>

                {/* Using this format because these components use routes injected props. We need to rework them with hooks */}
                <Route exact strict path="/swap" component={Swap} />
                <Route exact strict path="/swap/:outputCurrency" component={RedirectToSwap} />
                <Route exact strict path="/send" component={RedirectPathToSwapOnly} />
                <Route exact strict path="/find" component={PoolFinder} />
                <Route exact strict path="/liquidity" component={Liquidity} />
                <Route exact strict path="/create" component={RedirectToAddLiquidity} />
                <Route exact path="/add" component={AddLiquidity} />
                <Route exact path="/add/:currencyIdA" component={RedirectOldAddLiquidityPathStructure} />
                <Route exact path="/add/:currencyIdA/:currencyIdB" component={RedirectDuplicateTokenIds} />
                <Route exact path="/create" component={AddLiquidity} />
                <Route exact path="/create/:currencyIdA" component={RedirectOldAddLiquidityPathStructure} />
                <Route exact path="/create/:currencyIdA/:currencyIdB" component={RedirectDuplicateTokenIds} />
                <Route exact strict path="/remove/:tokens" component={RedirectOldRemoveLiquidityPathStructure} />
                <Route exact strict path="/remove/:currencyIdA/:currencyIdB" component={RemoveLiquidity} />

                {/* Redirect */}
                <Route path="/pool">
                  <Redirect to="/liquidity" />
                </Route>
                <Route path="/staking">
                  <Redirect to="/pools" />
                </Route>
                <Route path="/syrup">
                  <Redirect to="/pools" />
                </Route>
                <Route path="/nft">
                  <Redirect to="/collectibles" />
                </Route>

                {/* 404 */}
                {/* <Route component={NotFound} /> */}
                <Route component={Home} />
              </Switch>
            </SuspenseWithChunkError>
          </Menu>
        </Route>
      </Switch>
      <EasterEgg iterations={2} />
      <ToastListener />
      <DatePickerPortal />
    </BrowserRouter>
  )
}

export default React.memo(App)
